import { useState, useCallback, useEffect, useRef } from "react";
import { useApolloClient } from "@apollo/client";
import _ from "lodash";
import { getCalendarUsersByIdsQuery } from "MyCalendar/components/EventModalV2/modules/EventModalV2Query";
import useReduxState from "Hooks/useReduxState";
import { getOrganizationId } from "modules/CommonSelectors";

const selectEventModalCalendarItemType = state =>
  _.get(state, "myCalendar.eventModal.calendarEventItemType");

const LOADING = "loading";

const useVirtualizedUserDetails = ({ users, enabled }) => {
  const client = useApolloClient();
  const [detailsCache, setDetailsCache] = useState({});
  //getOrganizationId
  const organizationId = useReduxState(getOrganizationId);
  const calendarEventItemType = useReduxState(selectEventModalCalendarItemType);
  // Mirror of detailsCache readable synchronously inside effects without being a dep.
  const detailsCacheRef = useRef({});

  // Stable set of user ids — state so effect re-runs only when ids actually change.
  const [selectedUserIds, setSelectedUserIds] = useState(
    () => new Set(users.map(u => u.id))
  );

  // Keep selectedUserIds in sync; only triggers re-render when the id set changes.
  useEffect(() => {
    setSelectedUserIds(prev => {
      const next = new Set(users.map(u => u.id));
      if (prev.size === next.size && [...next].every(id => prev.has(id)))
        return prev;
      return next;
    });
  }, [users]);

  const fetchBatch = useCallback(
    async ids => {
      if (_.isEmpty(ids)) return;

      // Mark as in-flight immediately so concurrent effect runs skip them.
      ids.forEach(id => {
        detailsCacheRef.current[id] = LOADING;
      });
      setDetailsCache(prev => {
        const patch = {};
        ids.forEach(id => {
          if (!prev[id]) patch[id] = LOADING;
        });
        return _.isEmpty(patch) ? prev : { ...prev, ...patch };
      });

      try {
        const { data } = await client.query({
          query: getCalendarUsersByIdsQuery,
          variables: {
            filters: {
              includedUserIds: ids,
              ...(calendarEventItemType
                ? { eventType: calendarEventItemType }
                : {}),
            },
            organizationId,
          },
          fetchPolicy: "cache-first",
        });

        const nodes = _.chain(data)
          .get("node.calendarEventItemUsers.edges", [])
          .map(edge => edge?.node)
          .value();
        nodes.forEach(node => {
          if (node && node.id) detailsCacheRef.current[node.id] = node;
        });
        setDetailsCache(prev => {
          const patch = {};
          nodes.forEach(node => {
            if (node && node.id) patch[node.id] = node;
          });
          return { ...prev, ...patch };
        });
      } catch (err) {
        console.error(
          "useVirtualizedUserDetails: failed to fetch user details",
          err
        );
        ids.forEach(id => {
          if (detailsCacheRef.current[id] === LOADING)
            delete detailsCacheRef.current[id];
        });
        setDetailsCache(prev => {
          const patch = { ...prev };
          ids.forEach(id => {
            if (patch[id] === LOADING) delete patch[id];
          });
          return patch;
        });
      }
    },
    [client, calendarEventItemType]
  );

  // Fetch details for any ids not yet cached or in-flight.
  useEffect(() => {
    if (!enabled) return;
    const idsToFetch = [...selectedUserIds].filter(
      id => !detailsCacheRef.current[id]
    );
    if (_.isEmpty(idsToFetch)) return;
    fetchBatch(idsToFetch);
  }, [selectedUserIds, enabled, fetchBatch]);

  const onRangeChanged = useCallback(
    ({ startIndex, endIndex }) => {
      if (!enabled) return;
      const idsToFetch = users
        .slice(startIndex, endIndex + 1)
        .map(u => u.id)
        .filter(id => !detailsCacheRef.current[id]);
      if (_.isEmpty(idsToFetch)) return;
      fetchBatch(idsToFetch);
    },
    [users, enabled, fetchBatch]
  );

  const getUserDetails = useCallback(
    id => {
      const cached = detailsCache[id];
      if (cached === LOADING) return null;
      return cached || null;
    },
    [detailsCache]
  );

  return { onRangeChanged, getUserDetails };
};

export default useVirtualizedUserDetails;
